import React from 'react';
import { NintendoQuestSummary } from '@/lib/nintendo-quest';
import { useNintendoQuestTheme, useNintendoQuestThemeClasses } from '../contexts/NintendoQuestThemeContext';
import ThemeSelector from '../components/ThemeSelector';

// Import your actual existing components
import NintendoThermometer from '../NintendoThermometer';
import ActivityGrid from '../ActivityGrid';
import ShlokaJourney from '../ShlokaJourney';
import StreakCards from '../StreakCards';
import AchievementBadges from '../AchievementBadges';
import NextChallenges from '../NextChallenges';
import { DefaultTrajectoryCard } from '../DefaultTrajectoryCard';

interface DefaultThemeProps {
  data: NintendoQuestSummary;
}

const DefaultTheme: React.FC<DefaultThemeProps> = ({ data }) => {
  const { getVocabulary, themeConfig } = useNintendoQuestTheme();
  const { cardClass } = useNintendoQuestThemeClasses();

  const { overallProgress, todaysStats, currentWeek, shlokaJourney, streaks, achievements } = data;

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-600 via-blue-600 to-blue-800">
      <div className="container mx-auto px-4 py-8">
        
        {/* Header with Theme Selector */}
        <div className="flex justify-between items-start mb-8">
          <div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4 flex items-center gap-3">
              <span className="text-5xl">🎮</span>
              Rachit's Nintendo Switch Quest
            </h1>
            <p className="text-xl text-white/80">Complete analytics and detailed tracking</p>
          </div>
          <ThemeSelector />
        </div>

        {/* Top Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          {/* Today's Points */}
          <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
            <div className="flex items-center justify-between mb-2">
              <span className="text-white/80">{getVocabulary('daily')}</span>
              <span className="text-2xl">🔥</span>
            </div>
            <div className="text-3xl font-bold text-white">{todaysStats.pointsEarned}</div>
            <div className="text-sm text-white/60">Goal: {todaysStats.dailyGoalPoints}</div>
          </div>

          {/* Total Points */}
          <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
            <div className="flex items-center justify-between mb-2">
              <span className="text-white/80">{getVocabulary('total')}</span>
              <span className="text-2xl">⚡</span>
            </div>
            <div className="text-3xl font-bold text-white">{overallProgress.currentPoints}</div>
            <div className="text-sm text-white/60">of {overallProgress.targetPoints}</div>
          </div>

          {/* Money Earned */}
          <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
            <div className="flex items-center justify-between mb-2">
              <span className="text-white/80">Money Earned</span>
              <span className="text-2xl">💰</span>
            </div>
            <div className="text-3xl font-bold text-white">${overallProgress.moneyEquivalent.total}</div>
            <div className="text-sm text-white/60">Need: $300</div>
          </div>

          {/* Days Remaining */}
          <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
            <div className="flex items-center justify-between mb-2">
              <span className="text-white/80">Days Left</span>
              <span className="text-2xl">⏰</span>
            </div>
            <div className="text-3xl font-bold text-white">{overallProgress.daysRemaining}</div>
            <div className="text-sm text-white/60">Until Aug 10</div>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          
          {/* Left Column */}
          <div className="lg:col-span-1 space-y-6">
            {/* Nintendo Switch Thermometer */}
            <NintendoThermometer 
              currentPoints={overallProgress.currentPoints}
              targetPoints={overallProgress.targetPoints}
              moneyEquivalent={overallProgress.moneyEquivalent}
            />

            {/* Shloka Journey */}
            <ShlokaJourney journey={shlokaJourney} />
          </div>

          {/* Middle Column */}
          <div className="lg:col-span-1 space-y-6">
            {/* This Week's Activities */}
            {themeConfig.components.showActivityGrid && (
              // NEW (fixed):
              <ActivityGrid 
                allWeeks={data.allWeeks} 
                currentWeekIndex={data.currentWeekIndex}
                onWeekChange={(weekIndex) => {
                  // Optional: track week changes for analytics
                  console.log(`User navigated to week ${weekIndex + 1}`);
                }}
              />
            )}

            {/* Streaks */}
            <StreakCards streaks={streaks} />
          </div>

          {/* Right Column */}
          <div className="lg:col-span-1 space-y-6">
            {/* Achievements */}
            <AchievementBadges achievements={achievements} />

            <DefaultTrajectoryCard 
              currentPoints={overallProgress.currentPoints}
              targetPoints={overallProgress.targetPoints}
              dailyAverage={overallProgress.averagePointsPerDay}
              targetDate={data.settings.questEndDate} // "2025-08-10"
              daysActive={Math.ceil((new Date().getTime() - new Date(data.settings.questStartDate).getTime()) / (1000 * 60 * 60 * 24))}
            />

            {/* Next Challenges */}
            <NextChallenges challenges={data.nextChallenges} />
          </div>
        </div>

        {/* Footer */}
        <div className="text-center text-white/60 text-sm mt-12 py-4">
          Last updated: {new Date(data.lastUpdated).toLocaleString()} • Quest ends August 10, 2025
        </div>
      </div>
    </div>
  );
};

export default DefaultTheme;